import { useState } from "react";
import { ChevronDown, Clock } from "lucide-react";
import { cn } from "../../lib/utils";

export type TimeRange = "1h" | "today" | "24h" | "7d" | "14d" | "30d" | "all";

interface TimeRangeSelectorProps {
  value: TimeRange;
  onChange: (range: TimeRange) => void;
  className?: string;
}

const timeRangeOptions: { value: TimeRange; label: string }[] = [
  { value: "1h", label: "Last hour" },
  { value: "today", label: "Today" },
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
  { value: "14d", label: "Last 2 weeks" },
  { value: "30d", label: "Last 30 days" },
  { value: "all", label: "All time" },
];

export function TimeRangeSelector({ value, onChange, className }: TimeRangeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = timeRangeOptions.find((opt) => opt.value === value);

  return (
    <div className={cn("relative min-w-0", className)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors text-sm min-w-0 overflow-hidden"
      >
        <Clock className="h-4 w-4 shrink-0" />
        <span className="font-medium truncate min-w-0 flex-1 text-left">{selectedOption?.label ?? "Select range"}</span>
        <ChevronDown className={cn("h-4 w-4 shrink-0 transition-transform", isOpen && "rotate-180")} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 min-w-[160px] rounded-lg border border-border bg-card shadow-lg overflow-hidden">
            {timeRangeOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={cn(
                  "w-full px-3 py-2 text-left text-sm hover:bg-secondary/50 transition-colors",
                  value === option.value && "bg-secondary/30 text-primary"
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export { timeRangeOptions };
